import React, { useState } from 'react'
import './CallerInsightsPopup.scss'
import { ReactComponent as CloseIcon } from 'assets/svg/shared/close.svg'
import { integrationFavicons } from 'common/Enums'
import { useTranslation } from 'react-i18next'
import { getLeadTitle } from 'common/Helpers'
import { ReactComponent as OpenInNewTabIcon } from 'assets/svg/shared/open_in_new_tab.svg'
import { ShowButton, Accordion } from 'components/shared'
import { ReactComponent as ArrowDownIcon } from 'assets/svg/shared/arrow_down.svg'
import SimpleBar from 'simplebar-react'
import { useDispatch } from 'react-redux'
import { createIntegrationContact } from 'store/actions/call'
import { WhitelabelConfig } from 'hooks'

export function CallerInsightsPopup({ ...props }) {
    const dispatch = useDispatch()

    const {
        insights,
        integration,
        number = '',
        onClose = toggleDetailsShown,
        collapsable = false,
        className = '',
        showRecordsInTitle = false
    } = props

    const { name: integrationName, logo: integrationLogo, key, create_contact: isCreateContactAllowed } = integration

    const records = insights.records || insights.record || []

    const recordsFound = records.length > 0
    const isMultiRecord = recordsFound && records.length > 1
    const { isCreatingContact } = insights || false

    const [detailsShown, setDetailsShown] = useState(!collapsable)
    const { t } = useTranslation()

    function toggleDetailsShown() {
        setDetailsShown(!detailsShown)
    }

    const onCreateContact = () => {
        dispatch(createIntegrationContact({ number, driver: integration.key }))
    }

    const openLeadTitle = `${t('open_lead_at')} ${integration.name}`

    const insightsRecodrsDescription = recordsFound
        ? isMultiRecord
            ? `- ${records.length} ${t('records_found')}`
            : `- ${getLeadTitle(records[0].properties)}`
        : `- ${t('no_records_found')}`

    return (
        <div className={`caller__insights ${className}`}>
            <div className="caller__insights__header">
                {isCreatingContact ? (
                    <div className="skeleton"></div>
                ) : (
                    <>
                        <div className="caller__insights__header--left">
                            {integrationFavicons[key]}
                            <h2 className="caller__insights__header--title">{t('caller_insights')}</h2>
                            {showRecordsInTitle && (
                                <span className="caller__insights__header--records ml-1">
                                    {insightsRecodrsDescription}
                                </span>
                            )}
                        </div>
                        <div className="caller__insights__header--right">
                            {recordsFound ? (
                                !detailsShown && collapsable ? (
                                    <ShowButton
                                        onClick={toggleDetailsShown}
                                        color={WhitelabelConfig.colors.primary}
                                        imgWidth="20px"
                                    />
                                ) : (
                                    <button type="button" className="caller__insights--close" onClick={onClose}>
                                        <CloseIcon />
                                    </button>
                                )
                            ) : (
                                isCreateContactAllowed && (
                                    <button type="button" className="caller__insights--add" onClick={onCreateContact}>
                                        {t('create')}
                                    </button>
                                )
                            )}
                        </div>
                    </>
                )}
            </div>

            <SimpleBar className="caller__insights__body">
                {detailsShown &&
                    records.map((item, i) => {
                        const { company = '', email = '', lastmodifieddate = '', owner = {} } = item.properties

                        const header = (
                            <div className="caller__insights__block--row">
                                <div className="d-flex flex-grow-1">
                                    <h3 className="caller__insights--fullname">{getLeadTitle(item.properties)}</h3>
                                    <a
                                        className="button-integration ml-auto d-flex align-items-center"
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {integrationLogo ? (
                                            <img
                                                src={integrationLogo}
                                                width="54"
                                                className={`integration-logo ${key} ${integrationName.toLowerCase()} mr-2`}
                                                alt={`${integrationName} Logo`}
                                            />
                                        ) : (
                                            integrationName
                                        )}
                                        <OpenInNewTabIcon
                                            width="11"
                                            height="11"
                                            fill="currentColor"
                                            title={openLeadTitle}
                                        />
                                    </a>
                                </div>
                                {isMultiRecord && (
                                    <ArrowDownIcon
                                        className="toggler-icon ml-2"
                                        title="More details"
                                        width={14}
                                        height={8}
                                        fill="#a7a7a7"
                                    />
                                )}
                            </div>
                        )

                        const body = (
                            <div className="mt-2">
                                {company && (
                                    <div className="caller__insights__block--row">
                                        <span>{t('company')}</span>
                                        <span>{company}</span>
                                    </div>
                                )}
                                {email && (
                                    <div className="caller__insights__block--row">
                                        <span>{t('email')}</span>
                                        <span>{email}</span>
                                    </div>
                                )}
                                {lastmodifieddate && (
                                    <div className="caller__insights__block--row">
                                        <span>{t('last_modified')}</span>
                                        <span>
                                            {new Intl.DateTimeFormat('en-GB', {
                                                dateStyle: 'medium',
                                                timeStyle: 'short'
                                            }).format(new Date(lastmodifieddate))}
                                        </span>
                                    </div>
                                )}
                                {owner && owner?.ownerName && (
                                    <div className="caller__insights__block--row">
                                        <span>{t('owner')}</span>
                                        <span>{`${owner?.ownerName}`}</span>
                                    </div>
                                )}
                            </div>
                        )

                        return isMultiRecord ? (
                            <Accordion header={header} body={body} key={i} className="caller__insights__block" />
                        ) : (
                            <div className="caller__insights__block" key={i}>
                                {header}
                                {body}
                            </div>
                        )
                    })}
            </SimpleBar>
        </div>
    )
}
